import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tag, Plus, Trash2, Loader2, Ticket, BarChart3, TrendingUp, Users, CalendarClock, Mail, Copy, Rocket, RotateCcw } from 'lucide-react';

const BLANK = { code: '', kind: 'percent', value: 20, applies_to: 'all', max_redemptions: '', expires_at: '', note: '' };

export default function PromoManager() {
  const [promos, setPromos] = useState([]);
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [access, setAccess] = useState(null);
  const [founding, setFounding] = useState(null);
  const [foundingDate, setFoundingDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [waitlist, setWaitlist] = useState(null);
  const [announcing, setAnnouncing] = useState(false);
  const [launchDate, setLaunchDate] = useState('');
  const [scheduling, setScheduling] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, aRes, fRes] = await Promise.all([api.get('/admin/promos'), api.get('/admin/analytics'), api.get('/admin/founding')]);
      setPromos(pRes.data.promos || []);
      setPackages(pRes.data.packages || {});
      setAnalytics(aRes.data);
      setFounding(fRes.data);
      if (fRes.data?.deadline) setFoundingDate(String(fRes.data.deadline).slice(0, 10));
      try { const acc = await api.get('/admin/access'); setAccess(acc.data); } catch (e) {}
      try { const wl = await api.get('/admin/waitlist'); setWaitlist(wl.data); } catch (e) {}
    } catch (e) { /* non-admins never render this */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendLaunch = async () => {
    if (!window.confirm(`Send the launch announcement to ${waitlist?.launch_pending ?? 0} waitlist ${((waitlist?.launch_pending ?? 0) === 1) ? 'person' : 'people'}? Each person is emailed only once.`)) return;
    setAnnouncing(true);
    try {
      const res = await api.post('/admin/waitlist/announce', {});
      toast.success(`Launch email sent to ${res.data.sent} ${res.data.sent === 1 ? 'person' : 'people'}${res.data.failed ? ` (${res.data.failed} failed)` : ''}.`);
      const wl = await api.get('/admin/waitlist'); setWaitlist(wl.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send announcement'); } finally { setAnnouncing(false); }
  };

  const scheduleLaunch = async () => {
    if (!launchDate) { toast.error('Pick a date and time'); return; }
    const dt = new Date(launchDate);
    if (isNaN(dt.getTime())) { toast.error('Invalid date/time'); return; }
    if (dt.getTime() <= Date.now()) { toast.error('Pick a time in the future'); return; }
    setScheduling(true);
    try {
      await api.post('/admin/waitlist/schedule', { scheduled_at: dt.toISOString() });
      toast.success('Launch email scheduled');
      const wl = await api.get('/admin/waitlist'); setWaitlist(wl.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not schedule'); } finally { setScheduling(false); }
  };

  const cancelSchedule = async () => {
    try {
      await api.delete('/admin/waitlist/schedule');
      toast.success('Schedule cancelled');
      const wl = await api.get('/admin/waitlist'); setWaitlist(wl.data);
    } catch (e) { toast.error('Could not cancel'); }
  };

  const fmtSched = (iso) => { try { return new Date(iso).toLocaleString(); } catch (e) { return iso; } };

  const saveFoundingDate = async () => {
    if (!foundingDate) { toast.error('Pick a close date'); return; }
    setSavingDate(true);
    try {
      const res = await api.patch('/admin/founding', { deadline: foundingDate });
      setFounding({ ...founding, deadline: res.data.deadline, expired: res.data.expired });
      toast.success('Founding offer close date updated');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update date'); } finally { setSavingDate(false); }
  };

  const restartFounding = async () => {
    setRestarting(true);
    try {
      const res = await api.post('/admin/founding/restart', { days: 14 });
      setFounding({ ...founding, deadline: res.data.deadline, expired: false });
      setFoundingDate(String(res.data.deadline).slice(0, 10));
      toast.success('Countdown restarted', { description: '14 fresh days from right now.' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not restart'); }
    finally { setRestarting(false); }
  };

  const [sendingNudge, setSendingNudge] = useState(false);
  const [inviteEmails, setInviteEmails] = useState({});
  const [sendingCode, setSendingCode] = useState('');
  const sendInvite = async (code) => {
    const email = (inviteEmails[code] || '').trim();
    if (!email) { toast.error('Enter an email'); return; }
    setSendingCode(code);
    try {
      await api.post('/admin/access/send', { code, email });
      toast.success(`Invite emailed to ${email}`);
      setInviteEmails((m) => ({ ...m, [code]: '' }));
      const acc = await api.get('/admin/access'); setAccess(acc.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send invite'); } finally { setSendingCode(''); }
  };
  const sendNudge = async () => {
    setSendingNudge(true);
    try {
      const res = await api.post('/admin/founding-nudge');
      toast.success(`Closing-soon email sent to ${res.data.sent} ${res.data.sent === 1 ? 'person' : 'people'}${res.data.skipped ? ` (${res.data.skipped} already notified)` : ''}.`);
      const f = await api.get('/admin/founding'); setFounding(f.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send emails'); } finally { setSendingNudge(false); }
  };

  const create = async () => {
    if (!form.code.trim()) { toast.error('Give the code a name'); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        kind: form.kind,
        value: Number(form.value),
        applies_to: form.applies_to === 'all' ? ['all'] : [form.applies_to],
        active: true,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        expires_at: form.expires_at || null,
        note: form.note || '',
      };
      await api.post('/admin/promos', payload);
      toast.success(`Code ${payload.code} saved`);
      setForm(BLANK);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save code'); } finally { setSaving(false); }
  };

  const toggle = async (code) => {
    try { await api.patch(`/admin/promos/${code}`); load(); }
    catch (e) { toast.error('Could not update'); }
  };

  const remove = async (code) => {
    try { await api.delete(`/admin/promos/${code}`); toast.success('Removed'); load(); }
    catch (e) { toast.error('Could not delete'); }
  };

  const label = (p) => p.kind === 'percent' ? `${p.value}% off` : `$${p.value} off`;
  const scope = (p) => (p.applies_to || ['all']).includes('all') ? 'All plans' : (p.applies_to || []).map((k) => packages[k]?.label || k).join(', ');

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5" data-testid="promo-manager">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--accent))' }}><Ticket className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2"><h3 className="font-display font-600">Promo codes</h3><Badge variant="secondary" className="text-[10px]">Owner</Badge></div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Create launch & referral deals buyers can redeem at checkout.</p>
        </div>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="rounded-xl border border-[hsl(var(--border))] p-3 mb-3" data-testid="promo-analytics">
          <div className="flex items-center gap-2 mb-3"><BarChart3 className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} /><p className="text-sm font-600">Deal performance</p></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="stat-orders"><p className="font-display text-xl font-700">{analytics.totals.orders}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">paid orders</p></div>
            <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="stat-revenue"><p className="font-display text-xl font-700" style={{ color: 'hsl(var(--primary))' }}>${analytics.totals.revenue}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">revenue</p></div>
            <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="stat-referrals"><p className="font-display text-xl font-700">{analytics.totals.referral_conversions}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">referrals</p></div>
            <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center" data-testid="stat-waitlist"><p className="font-display text-xl font-700">{analytics.totals.waitlist ?? 0}</p><p className="text-[10px] text-[hsl(var(--muted-foreground))]">waitlist</p></div>
          </div>

          <p className="text-[11px] font-600 text-[hsl(var(--muted-foreground))] flex items-center gap-1 mb-1.5"><TrendingUp className="h-3.5 w-3.5" /> Top codes</p>
          {analytics.promo_performance.length === 0 ? (
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] py-1">No purchases with a code yet.</p>
          ) : (
            <div className="space-y-1">
              {analytics.promo_performance.slice(0, 6).map((r) => (
                <div key={r.code} className="flex items-center gap-2 text-xs rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5" data-testid={`analytics-code-${r.code}`}>
                  <span className="font-mono font-600">{r.code}</span>
                  <Badge variant="secondary" className="text-[9px]">{r.type === 'referral' ? 'referral' : 'promo'}</Badge>
                  <span className="ml-auto text-[hsl(var(--muted-foreground))]">{r.orders} sign-up{r.orders === 1 ? '' : 's'}</span>
                  <span className="font-600" style={{ color: 'hsl(var(--primary))' }}>${r.revenue}</span>
                </div>
              ))}
            </div>
          )}

          {analytics.referral_performance.length > 0 && (
            <>
              <p className="text-[11px] font-600 text-[hsl(var(--muted-foreground))] flex items-center gap-1 mt-3 mb-1.5"><Users className="h-3.5 w-3.5" /> Top referrers</p>
              <div className="space-y-1">
                {analytics.referral_performance.slice(0, 5).map((r) => (
                  <div key={r.referrer_id} className="flex items-center gap-2 text-xs rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5" data-testid={`analytics-referrer-${r.referrer_id}`}>
                    <span className="font-600 truncate">{r.name}</span>
                    <span className="font-mono text-[10px] text-[hsl(var(--muted-foreground))] truncate">{r.code}</span>
                    <span className="ml-auto text-[hsl(var(--muted-foreground))]">{r.conversions} joined</span>
                    <span className="font-600">{r.credits_earned} cr</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Founding offer close date */}
      {founding && (
        <div className="rounded-xl border border-[hsl(var(--border))] p-3 mb-3" data-testid="founding-date-box">
          <div className="flex items-center gap-2 mb-2"><CalendarClock className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} /><p className="text-sm font-600">Founding offer close date</p></div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">The 100-seat offer closes on this date (or when seats run out — whichever comes first). A live countdown shows on the landing and upgrade pages.</p>
          <div className="flex items-center gap-2">
            <Input type="date" value={foundingDate} onChange={(e) => setFoundingDate(e.target.value)} className="h-9 rounded-lg text-sm flex-1" data-testid="founding-date-input" />
            <Button onClick={saveFoundingDate} disabled={savingDate} variant="secondary" className="h-9 rounded-lg border border-[hsl(var(--border))] shrink-0" data-testid="founding-date-save">{savingDate ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save date'}</Button>
          </div>
          <Button onClick={restartFounding} disabled={restarting} className="mt-2 h-9 rounded-lg gap-1.5 text-white" style={{ background: 'hsl(var(--primary))' }} data-testid="founding-restart-button">{restarting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Restart 14-day countdown</Button>
          {founding.expired && <p className="text-[11px] mt-1.5" style={{ color: 'hsl(var(--destructive))' }}>This offer has closed. Pick a future date to reopen it.</p>}

          {/* Closing-soon email nudge */}
          <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
            <div className="flex items-center gap-2 mb-1.5">
              <Mail className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
              <p className="text-sm font-600">Closing-soon email</p>
              <span className="text-[10px] px-2 py-0.5 rounded-full" style={founding.email_configured ? { background: 'hsl(var(--success) / 0.15)', color: 'hsl(var(--success))' } : { background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))' }} data-testid="email-status">
                {founding.email_configured ? 'Resend connected' : 'Add Resend key'}
              </span>
            </div>
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">
              A gentle "last 48 hours" nudge goes to free users automatically once the offer is within 48h — or send it now. Each person is emailed only once.
              {' '}<span data-testid="nudge-eligible"><b>{founding.nudge_eligible}</b> eligible</span>{founding.nudge_sent ? <> · <b>{founding.nudge_sent}</b> already emailed</> : null}.
            </p>
            <Button onClick={sendNudge} disabled={sendingNudge || !founding.email_configured || founding.expired} variant="secondary" className="h-9 rounded-lg border border-[hsl(var(--border))] gap-1.5" data-testid="send-nudge-button">
              {sendingNudge ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send closing-soon nudge now
            </Button>
            {!founding.email_configured && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">Add <code>RESEND_API_KEY</code> in the backend to enable sending.</p>}
          </div>
        </div>
      )}

      {/* Launch announcement to waitlist */}
      {waitlist && (
        <div className="rounded-xl border border-[hsl(var(--border))] p-3 mb-3" data-testid="launch-announce-box">
          <div className="flex items-center gap-2 mb-1.5">
            <Rocket className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
            <p className="text-sm font-600">Launch announcement</p>
            <span className="text-[10px] px-2 py-0.5 rounded-full" style={waitlist.email_configured ? { background: 'hsl(var(--success) / 0.15)', color: 'hsl(var(--success))' } : { background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))' }} data-testid="launch-email-status">
              {waitlist.email_configured ? 'Resend connected' : 'Add Resend key'}
            </span>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">
            Email your waitlist that Larkwell is live. Each person is emailed only once.
            {' '}<span data-testid="launch-pending"><b>{waitlist.launch_pending ?? 0}</b> not yet emailed</span>{waitlist.launch_sent ? <> · <b>{waitlist.launch_sent}</b> already sent</> : null} · <b>{waitlist.count ?? 0}</b> total.
          </p>
          <Button onClick={sendLaunch} disabled={announcing || !waitlist.email_configured || (waitlist.launch_pending ?? 0) === 0} className="h-9 rounded-lg gap-1.5" style={{ background: 'hsl(var(--primary))' }} data-testid="send-launch-button">
            {announcing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Send launch email now
          </Button>
          {/* Schedule for later */}
          <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
            {waitlist.schedule?.status === 'scheduled' ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="launch-scheduled-row">
                <span className="inline-flex items-center gap-1.5 text-sm font-600" style={{ color: 'hsl(var(--primary))' }}><CalendarClock className="h-4 w-4" /> Scheduled for {fmtSched(waitlist.schedule.scheduled_at)}</span>
                <Button onClick={cancelSchedule} variant="secondary" className="h-8 rounded-lg border border-[hsl(var(--border))] text-xs ml-auto" data-testid="cancel-schedule-button">Cancel</Button>
              </div>
            ) : (
              <>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1.5 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Or schedule it for later (sends automatically at that time):</p>
                <div className="flex items-center gap-2">
                  <Input type="datetime-local" value={launchDate} onChange={(e) => setLaunchDate(e.target.value)} className="h-9 rounded-lg text-sm flex-1" data-testid="launch-schedule-input" />
                  <Button onClick={scheduleLaunch} disabled={scheduling || !waitlist.email_configured || !launchDate} variant="secondary" className="h-9 rounded-lg border border-[hsl(var(--border))] gap-1.5 shrink-0" data-testid="schedule-launch-button">{scheduling ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Schedule</Button>
                </div>
                {waitlist.schedule?.status === 'sent' && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">Last scheduled blast was sent.</p>}
              </>
            )}
          </div>
          {!waitlist.email_configured && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">Add <code>RESEND_API_KEY</code> in the backend to enable sending.</p>}
        </div>
      )}

      {/* Invite codes (free access) */}
      {access?.codes?.length > 0 && (
        <div className="rounded-xl border border-[hsl(var(--border))] p-3 mb-3" data-testid="invite-codes-box">
          <div className="flex items-center gap-2 mb-1.5"><Ticket className="h-4 w-4" style={{ color: 'hsl(var(--accent))' }} /><p className="text-sm font-600">Free access invites</p></div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">One-time codes that grant free lifetime Pro (they don't use founding seats). Redeemed on the Upgrade page.</p>
          <div className="space-y-1.5">
            {access.codes.map((c) => (
              <div key={c.code} className="rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5" data-testid={`invite-row-${c.code}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[hsl(var(--muted-foreground))] w-20 shrink-0">{c.label}</span>
                  <span className="font-mono font-600 text-sm">{c.code}</span>
                  {c.used_by ? (
                    <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'hsl(var(--secondary))', color: 'hsl(var(--muted-foreground))' }}>Used{c.used_email ? ` · ${c.used_email}` : ''}</span>
                  ) : (
                    <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success(`Copied ${c.code}`); }} className="ml-auto text-[11px] inline-flex items-center gap-1 text-[hsl(var(--primary))] hover:underline" data-testid={`invite-copy-${c.code}`}><Copy className="h-3 w-3" /> Copy</button>
                  )}
                </div>
                {!c.used_by && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input value={inviteEmails[c.code] || ''} onChange={(e) => setInviteEmails((m) => ({ ...m, [c.code]: e.target.value }))} placeholder="friend@email.com" className="h-8 rounded-lg text-xs flex-1" data-testid={`invite-email-${c.code}`} />
                    <Button onClick={() => sendInvite(c.code)} disabled={sendingCode === c.code} variant="secondary" className="h-8 rounded-lg border border-[hsl(var(--border))] text-xs gap-1 shrink-0" data-testid={`invite-send-${c.code}`}>{sendingCode === c.code ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Email invite</Button>
                  </div>
                )}
                {c.sent_to && !c.used_by && <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Last emailed to {c.sent_to}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create form */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH25" className="h-9 rounded-lg mt-1 uppercase text-sm" data-testid="promo-new-code" /></div>
          <div>
            <Label className="text-xs">Discount type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="h-9 rounded-lg mt-1" data-testid="promo-new-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Percent off (%)</SelectItem>
                <SelectItem value="fixed">Fixed amount ($)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">{form.kind === 'percent' ? 'Percent (1-100)' : 'Amount in USD'}</Label><Input type="number" min="1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="h-9 rounded-lg mt-1 text-sm" data-testid="promo-new-value" /></div>
          <div>
            <Label className="text-xs">Applies to</Label>
            <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
              <SelectTrigger className="h-9 rounded-lg mt-1" data-testid="promo-new-applies"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All plans</SelectItem>
                {Object.entries(packages).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs">Max redemptions (optional)</Label><Input type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} placeholder="Unlimited" className="h-9 rounded-lg mt-1 text-sm" data-testid="promo-new-max" /></div>
          <div><Label className="text-xs">Expires (optional)</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="h-9 rounded-lg mt-1 text-sm" data-testid="promo-new-expires" /></div>
        </div>
        <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Internal note (e.g. Product Hunt launch)" className="h-9 rounded-lg text-sm" data-testid="promo-new-note" />
        <Button onClick={create} disabled={saving} className="rounded-xl gap-1.5 w-full" style={{ background: 'hsl(var(--primary))' }} data-testid="promo-create-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create code</Button>
      </div>

      {/* Existing codes */}
      <div className="mt-4 space-y-2">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--muted-foreground))]" /></div>
        ) : promos.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-3">No codes yet — create your first deal above.</p>
        ) : promos.map((p) => (
          <div key={p.code} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`promo-row-${p.code}`}>
            <Tag className="h-4 w-4 shrink-0" style={{ color: p.active ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))' }} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-600 text-sm">{p.code}</span>
                <Badge variant="secondary" className="text-[10px]">{label(p)}</Badge>
                <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{scope(p)}</span>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                {p.redemptions || 0}{p.max_redemptions ? ` / ${p.max_redemptions}` : ''} used{p.expires_at ? ` · expires ${String(p.expires_at).slice(0, 10)}` : ''}{p.note ? ` · ${p.note}` : ''}
              </p>
            </div>
            <Switch checked={p.active} onCheckedChange={() => toggle(p.code)} data-testid={`promo-toggle-${p.code}`} />
            <button onClick={() => remove(p.code)} data-testid={`promo-delete-${p.code}`} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] transition-colors"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
